import {
    useEffect,
    useState,
    useMemo,
    useRef
} from "react";
import {
    useWindowDimensions,
    TouchableOpacity,
    Platform,
    Image,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitSnackBar,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    PageContainer,
    TextAreaInput,
    SelectBox,
    Seperator,
    TextInput,
    Dialog,
    Button,
    Switch,
    Text
} from "ncore-ui-kit";
import {
    useNavigation
} from "@react-navigation/native";
import type RootStackParamList from "../../../../navigation/type";
import {
    type NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    ImageIcon,
    SaveIcon
} from "lucide-react-native";
import RESTServiceContext from "../../../../services/restAPI/context";

const CompanyCreate = () => {
    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const [
        title,
        setTitle
    ] = useState<string>("");

    const [
        aboutUs,
        setAboutUs
    ] = useState<string>("");

    const [
        mail,
        setMail
    ] = useState<string>("");

    const [
        address,
        setAddress
    ] = useState<string>("");

    const [
        taxOffice,
        setTaxOffice
    ] = useState<string>("");

    const [
        taxNumber,
        setTaxNumber
    ] = useState<string>("");

    const [
        website,
        setWebsite
    ] = useState<string>("");

    const [
        foundationYear,
        setFoundationYear
    ] = useState<string>("");

    const [
        isExports,
        setIsExports
    ] = useState<boolean>(false);

    const [
        phoneNumber,
        setPhoneNumber
    ] = useState<string>("");

    const [
        neighborhood,
        setNeighborhood
    ] = useState<string>("");

    const [
        productsBoughtSoldStr,
        setProductsBoughtSoldStr
    ] = useState<string>("");

    const [
        logoPreview,
        setLogoPreview
    ] = useState<string>("");

    const [
        logoFile,
        setLogoFile
    ] = useState<File | null>(null);

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState<boolean>(false);

    const [
        isDialogVisible,
        setIsDialogVisible
    ] = useState<boolean>(false);

    // Dropdown Data
    const [
        positionsData,
        setPositionsData
    ] = useState<{ title: string; value: string }[]>([]);

    const [
        usersData,
        setUsersData
    ] = useState<{ title: string; value: string }[]>([]);

    const [
        sectorsData,
        setSectorsData
    ] = useState<{ title: string; value: string }[]>([]);

    const [
        citiesData,
        setCitiesData
    ] = useState<{ title: string; value: string }[]>([]);

    const [
        districtsData,
        setDistrictsData
    ] = useState<{ title: string; value: string }[]>([]);

    // Selected Dropdown Values
    const [
        ownerPositionID,
        setOwnerPositionID
    ] = useState<string>("");

    const [
        ownerID,
        setOwnerID
    ] = useState<string>("");

    const [
        sectorIDs,
        setSectorIDs
    ] = useState<string[]>([]);

    const [
        cityID,
        setCityID
    ] = useState<string>("");

    const [
        districtID,
        setDistrictID
    ] = useState<string>("");

    const logoInputRef = useRef<HTMLInputElement | null>(null);
    const positionSelectRef = useRef<import("react").ElementRef<typeof SelectBox>>(null);
    const ownerSelectRef = useRef<import("react").ElementRef<typeof SelectBox>>(null);
    const sectorSelectRef = useRef<import("react").ElementRef<typeof SelectBox>>(null);
    const citySelectRef = useRef<import("react").ElementRef<typeof SelectBox>>(null);
    const districtSelectRef = useRef<import("react").ElementRef<typeof SelectBox>>(null);

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const usersResponse = await RESTServiceContext.action("getAllUsersForAdmin", {});
                if (usersResponse.payload) {
                    const mappedUsers = usersResponse.payload.map(u => ({
                        title: u.fullName || `${u.firstName} ${u.lastName}`,
                        value: u._id
                    }));
                    setUsersData(mappedUsers);
                    ownerSelectRef.current?.updateData(mappedUsers, []);
                }

                const positionsResponse = await RESTServiceContext.action("getPositions", null);
                if (positionsResponse.payload) {
                    const mappedPositions = positionsResponse.payload.map((p: { value: string; _id: string }) => ({
                        title: p.value,
                        value: p._id
                    }));
                    setPositionsData(mappedPositions);
                    positionSelectRef.current?.updateData(mappedPositions, []);
                }

                const sectorsResponse = await RESTServiceContext.action("getSectors", null);
                if (sectorsResponse.payload) {
                    const mappedSectors = sectorsResponse.payload.map(s => ({
                        title: s.value,
                        value: s._id
                    }));
                    setSectorsData(mappedSectors);
                    sectorSelectRef.current?.updateData(mappedSectors, []);
                }

                const citiesResponse = await RESTServiceContext.action("getCities", null);
                if (citiesResponse.payload) {
                    const mappedCities = citiesResponse.payload.map(c => ({
                        title: c.value,
                        value: c._id
                    }));
                    setCitiesData(mappedCities);
                    citySelectRef.current?.updateData(mappedCities, []);
                }
            } catch (error) {
                console.error("Error fetching initial data", error);
            }
        };
        fetchInitialData();
    }, []);

    const handleCityChange = async (selected: unknown[]) => {
        if (selected.length > 0 && selected[0]) {
            const item = selected[0] as { __key?: string, value?: string };
            const cID = item.__key || item.value || "";
            setCityID(cID);
            setDistrictID("");
            if (cID) {
                try {
                    const distResponse = await RESTServiceContext.action("getDistricts", {
                        cityID: cID
                    });
                    if (distResponse.payload) {
                        const mappedDistricts = distResponse.payload.map((d: { value: string; _id: string }) => ({
                            title: d.value,
                            value: d._id
                        }));
                        setDistrictsData(mappedDistricts);
                        districtSelectRef.current?.updateData(mappedDistricts, []);
                    }
                } catch (error) {
                    console.error("Error fetching districts", error);
                }
            } else {
                setDistrictsData([]);
                districtSelectRef.current?.updateData([], []);
            }
        } else {
            setCityID("");
            setDistrictID("");
            setDistrictsData([]);
            districtSelectRef.current?.updateData([], []);
        }
    };

    const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const maxFileSize = 5 * 1024 * 1024;
            if (file.size > maxFileSize) {
                NCoreUIKitSnackBar.open({
                    title: localize("members-invalid-file-size") || "Dosya boyutu 5 MB'dan küçük olmalıdır."
                });
                return;
            }
            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const uploadPhoto = async (
        companyID: string,
        file: File
    ) => {
        try {
            const result = await RESTServiceContext.action("getUploadTokenForCompanyPhoto", {
                companyID: companyID
            });
            const uploadResult = result as unknown as { payload?: { token: string } };
            if (uploadResult.payload?.token) {
                const formData = new FormData();
                formData.append("file", file);
                const response = await fetch(`${process.env.STORAGE_URL}/upload`, {
                    headers: {
                        Authorization: uploadResult.payload.token
                    },
                    body: formData,
                    method: "POST"
                });
                if (!response.ok) {
                    NCoreUIKitSnackBar.open({
                        title: "Storage upload failed: " + response.status,
                        type: "danger"
                    });
                }
            } else {
                NCoreUIKitSnackBar.open({
                    title: "No upload token returned!",
                    type: "danger"
                });
            }
        } catch (err: unknown) {
            const error = err as Error;
            console.error(error);
            NCoreUIKitSnackBar.open({
                title: "Upload error: " + (error.message || "Unknown error"),
                type: "danger"
            });
        }
    };

    const isFormValid = useMemo(() => {
        if (title.trim().length < 2 || title.trim().length > 100) { console.log("Invalid title", title.length); return false; }
        if (aboutUs.trim().length < 3 || aboutUs.trim().length > 1000) { console.log("Invalid aboutUs", aboutUs.length); return false; }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (mail.trim().length < 6 || mail.trim().length > 100 || !emailRegex.test(mail.trim())) { console.log("Invalid mail", mail); return false; }

        if (address.trim().length < 2 || address.trim().length > 300) { console.log("Invalid address", address.length); return false; }
        if (taxOffice.trim().length < 5 || taxOffice.trim().length > 200) { console.log("Invalid taxOffice", taxOffice.length); return false; }
        if (taxNumber.trim().length < 10 || taxNumber.trim().length > 11) { console.log("Invalid taxNumber", taxNumber.length); return false; }

        if (website.trim().length < 2 || website.trim().length > 100) { console.log("Invalid website length", website.length); return false; }
        if (!website.includes(".")) { console.log("Invalid website (needs a dot)", website); return false; }

        const fYear = parseInt(foundationYear, 10);
        if (isNaN(fYear) || fYear < 1923 || fYear > 2026) { console.log("Invalid foundationYear", foundationYear); return false; }

        const cleanPhone = phoneNumber.replace(/\s/g, "");
        if (cleanPhone.length < 10) { console.log("Invalid phoneNumber", cleanPhone.length); return false; }

        if (!cityID) { console.log("Invalid cityID"); return false; }
        if (!districtID) { console.log("Invalid districtID"); return false; }
        if (!neighborhood.trim()) { console.log("Invalid neighborhood"); return false; }

        if (sectorIDs.length === 0) { console.log("Invalid sectorIDs"); return false; }

        const productsBoughtSold = productsBoughtSoldStr.split(",").map(p => p.trim()).filter(p => p.length > 0);
        if (productsBoughtSold.length === 0) { console.log("Invalid productsBoughtSold 0 items"); return false; }
        for (const p of productsBoughtSold) {
            if (p.length < 3 || p.length > 1000) { console.log("Invalid product item length", p); return false; }
        }

        if (ownerID && !ownerPositionID) { console.log("Invalid ownerPositionID"); return false; }

        return true;
    }, [
        title,
        aboutUs,
        mail,
        address,
        taxOffice,
        taxNumber,
        website,
        foundationYear,
        phoneNumber,
        cityID,
        districtID,
        neighborhood,
        sectorIDs,
        productsBoughtSoldStr,
        ownerID,
        ownerPositionID
    ]);

    const handleConfirmSave = async () => {
        setIsDialogVisible(false);
        try {
            setIsSubmitting(true);
            const productsBoughtSold = productsBoughtSoldStr.split(",").map(p => p.trim()).filter(p => p.length > 0);

            const result = await RESTServiceContext.action("createCompanyForAdmin", {
                foundationYear: parseInt(foundationYear, 10),
                productsBoughtSold: productsBoughtSold,
                ownerPositionID: ownerPositionID,
                neighborhood: neighborhood,
                phoneNumber: phoneNumber,
                districtID: districtID,
                isExports: isExports,
                sectorIDs: sectorIDs,
                taxNumber: taxNumber,
                taxOffice: taxOffice,
                aboutUs: aboutUs,
                address: address,
                ownerID: ownerID,
                website: website,
                cityID: cityID,
                title: title,
                mail: mail
            });

            if (result.code === 200 || result.code === 201 || result.payload) {
                const newCompanyID = result.payload?.companyID;
                if (newCompanyID && logoFile) {
                    await uploadPhoto(newCompanyID, logoFile);
                }
                NCoreUIKitSnackBar.open({
                    title: localize("companies-success-create") || "Şirket başarıyla oluşturuldu."
                });
                navigation.navigate("CompaniesList");
            } else {
                NCoreUIKitSnackBar.open({
                    title: localize("companies-error-save") || "Şirket oluşturulamadı."
                });
            }
        } catch (error) {
            console.error(error);
            NCoreUIKitSnackBar.open({
                title: localize("companies-error-save") || "Şirket oluşturulamadı."
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = async () => {
        if (!title.trim() || (ownerID && !ownerPositionID) || !mail.trim() || !phoneNumber.trim()) {
            NCoreUIKitSnackBar.open({
                title: localize("companies-required-fields") || "Lütfen zorunlu alanları doldurun."
            });
            return;
        }

        setIsDialogVisible(true);
    };

    const renderHeader = () => {
        return <View
            style={stylesheet.headerContainer}
        >
            <Text
                variant="titleLargeSize"
            >
                {localize("companies-create-page-title")}
            </Text>
        </View>;
    };

    const renderImagesSection = () => {
        return <View
            style={[
                stylesheet.sectionContainer,
                {
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.sectionHeader,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Text
                    style={stylesheet.sectionTitle}
                    variant="titleMediumSize"
                >
                    {localize("companies-images-section-title") || "Şirket Logosu"}
                </Text>
                <Text
                    style={[
                        stylesheet.sectionSubtitle,
                        {
                            color: colors.content.text.mid
                        }
                    ]}
                    variant="bodyMediumSize"
                >
                    {localize("companies-images-section-subtitle") || "Şirket logosunu buradan yükleyebilirsiniz."}
                </Text>
            </View>
            <View
                style={[
                    stylesheet.uploadRow,
                    {
                        flexDirection: isMobile ? "column" : "row",
                        gap: spaces.spacingLg
                    }
                ]}
            >
                {
                    Platform.OS === "web" ?
                        <input
                            onChange={handleLogoSelect}
                            style={{
                                display: "none"
                            }}
                            ref={logoInputRef}
                            accept="image/*"
                            type="file"
                        />
                    :
                        null
                }
                <TouchableOpacity
                    style={[
                        stylesheet.logoUploadBox,
                        {
                            backgroundColor: colors.project.brandSurface,
                            borderColor: colors.project.brandBorder,
                            width: isMobile ? "100%" : 160,
                            borderWidth: borders.seperator,
                            borderRadius: radiuses.md
                        }
                    ]}
                    onPress={() => logoInputRef.current?.click()}
                    activeOpacity={0.8}
                >
                    {
                        logoPreview ?
                            <Image
                                style={stylesheet.uploadedImage}
                                source={{
                                    uri: logoPreview
                                }}
                                resizeMode="cover"
                            />
                        :
                            <View
                                style={[
                                    stylesheet.uploadPlaceholder,
                                    {
                                        gap: spaces.spacingXs
                                    }
                                ]}
                            >
                                <ImageIcon
                                    color={colors.content.icon.mid}
                                    size={32}
                                />
                                <Text
                                    style={stylesheet.uploadLabel}
                                    variant="bodyMediumSize"
                                >
                                    {localize("companies-upload-logo") || "Logo Yükle"}
                                </Text>
                            </View>
                    }
                </TouchableOpacity>
            </View>
        </View>;
    };

    const renderBasicInfoSection = () => {
        return <View
            style={[
                stylesheet.sectionContainer,
                {
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.sectionHeader,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Text
                    style={stylesheet.sectionTitle}
                    variant="titleMediumSize"
                >
                    {localize("companies-basic-info-title") || "Temel Bilgiler"}
                </Text>
            </View>
            <TextInput
                type={title.length > 0 && (title.trim().length < 2 || title.trim().length > 100) ? "danger" : "default"}
                placeholder={localize("companies-title-placeholder") || "Şirket Adı"}
                title={localize("companies-title-input") || "Şirket Adı *"}
                onChangeText={(text) => setTitle(text)}
                spreadBehaviour="stretch"
                isRequired={true}
                value={title}
            />
            <TextInput
                type={mail.length > 0 && (mail.trim().length < 6 || mail.trim().length > 100 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail.trim())) ? "danger" : "default"}
                placeholder={localize("companies-mail-placeholder") || "E-posta"}
                title={localize("companies-mail-input") || "E-posta *"}
                onChangeText={(text) => setMail(text)}
                spreadBehaviour="stretch"
                isRequired={true}
                value={mail}
            />
            <TextInput
                type={phoneNumber.length > 0 && phoneNumber.trim().length < 10 ? "danger" : "default"}
                placeholder={localize("companies-phone-placeholder") || "Telefon"}
                title={localize("companies-phone-input") || "Telefon *"}
                onChangeText={(text) => setPhoneNumber(text)}
                spreadBehaviour="stretch"
                value={phoneNumber}
                isRequired={true}
            />
            <TextInput
                type={website.length > 0 && (website.trim().length < 2 || website.trim().length > 100 || !website.includes(".")) ? "danger" : "default"}
                placeholder={localize("companies-website-placeholder") || "Web Sitesi"}
                title={localize("companies-website-input") || "Web Sitesi *"}
                onChangeText={(text) => setWebsite(text)}
                spreadBehaviour="stretch"
                isRequired={true}
                value={website}
            />
            <TextInput
                type={foundationYear.length > 0 && (isNaN(parseInt(foundationYear, 10)) || parseInt(foundationYear, 10) < 1923 || parseInt(foundationYear, 10) > 2026) ? "danger" : "default"}
                placeholder={localize("companies-foundation-year-placeholder") || "Kuruluş Yılı (Örn: 2020)"}
                title={localize("companies-foundation-year-input") || "Kuruluş Yılı *"}
                onChangeText={(text) => setFoundationYear(text)}
                spreadBehaviour="stretch"
                value={foundationYear}
                isRequired={true}
            />
            <SelectBox
                titleExtractor={(i: unknown) => (i as { title: string }).title}
                onChange={(selected) => {
                    const ids = selected.map(s => {
                        const item = s as { __key?: string, value?: string };
                        return item.__key || item.value || "";
                    });
                    setSectorIDs(ids);
                }}
                keyExtractor={(i: unknown) => (i as { value: string }).value}
                title={localize("companies-sector-select") || "Sektörler *"}
                spreadBehaviour="stretch"
                isMultipleSelect={true}
                ref={sectorSelectRef}
                data={sectorsData}
                isRequired={true}
                maxChoice={-1}
            />
            <TextAreaInput
                type={aboutUs.length > 0 && (aboutUs.trim().length < 3 || aboutUs.trim().length > 1000) ? "danger" : "default"}
                placeholder={localize("companies-about-us-placeholder") || "Hakkımızda"}
                title={localize("companies-about-us-input") || "Hakkımızda *"}
                onChangeText={(text) => setAboutUs(text)}
                isUpdateOnRealtime={true}
                spreadBehaviour="stretch"
                isRequired={true}
                numberOfLines={4}
                value={aboutUs}
            />
            <TextInput
                type={productsBoughtSoldStr.length > 0 && (productsBoughtSoldStr.split(",").map(p => p.trim()).filter(p => p.length > 0).length === 0 || productsBoughtSoldStr.split(",").map(p => p.trim()).filter(p => p.length > 0).some(p => p.length < 3 || p.length > 1000)) ? "danger" : "default"}
                placeholder={localize("companies-products-placeholder") || "Alınan/Satılan Ürünler (Virgülle ayırın)"}
                title={localize("companies-products-input") || "Alınan/Satılan Ürünler *"}
                onChangeText={(text) => setProductsBoughtSoldStr(text)}
                value={productsBoughtSoldStr}
                spreadBehaviour="stretch"
                isRequired={true}
            />
        </View>;
    };

    const renderAddressTaxSection = () => {
        return <View
            style={[
                stylesheet.sectionContainer,
                {
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.sectionHeader,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Text
                    style={stylesheet.sectionTitle}
                    variant="titleMediumSize"
                >
                    {localize("companies-address-tax-title") || "Adres ve Vergi Bilgileri"}
                </Text>
            </View>
            <SelectBox
                titleExtractor={(i: unknown) => (i as { title: string }).title}
                keyExtractor={(i: unknown) => (i as { value: string }).value}
                title={localize("companies-city-select") || "Şehir *"}
                onChange={handleCityChange}
                spreadBehaviour="stretch"
                ref={citySelectRef}
                data={citiesData}
                isRequired={true}
            />
            <SelectBox
                onChange={(selected) => {
                    if (selected.length > 0 && selected[0]) {
                        const item = selected[0] as { __key?: string, value?: string };
                        setDistrictID(item.__key || item.value || "");
                    } else {
                        setDistrictID("");
                    }
                }}
                titleExtractor={(i: unknown) => (i as { title: string }).title}
                keyExtractor={(i: unknown) => (i as { value: string }).value}
                title={localize("companies-district-select") || "İlçe *"}
                spreadBehaviour="stretch"
                ref={districtSelectRef}
                data={districtsData}
                isRequired={true}
            />
            <TextInput
                type={neighborhood.length > 0 && neighborhood.trim().length === 0 ? "danger" : "default"}
                placeholder={localize("companies-neighborhood-placeholder") || "Mahalle"}
                title={localize("companies-neighborhood-input") || "Mahalle *"}
                onChangeText={(text) => setNeighborhood(text)}
                spreadBehaviour="stretch"
                value={neighborhood}
                isRequired={true}
            />
            <TextAreaInput
                type={address.length > 0 && (address.trim().length < 2 || address.trim().length > 300) ? "danger" : "default"}
                placeholder={localize("companies-address-placeholder") || "Açık Adres"}
                title={localize("companies-address-input") || "Açık Adres *"}
                onChangeText={(text) => setAddress(text)}
                isUpdateOnRealtime={true}
                spreadBehaviour="stretch"
                isRequired={true}
                numberOfLines={3}
                value={address}
            />
            <TextInput
                type={taxOffice.length > 0 && (taxOffice.trim().length < 5 || taxOffice.trim().length > 200) ? "danger" : "default"}
                placeholder={localize("companies-tax-office-placeholder") || "Vergi Dairesi"}
                title={localize("companies-tax-office-input") || "Vergi Dairesi *"}
                onChangeText={(text) => setTaxOffice(text)}
                spreadBehaviour="stretch"
                isRequired={true}
                value={taxOffice}
            />
            <TextInput
                type={taxNumber.length > 0 && (taxNumber.trim().length < 10 || taxNumber.trim().length > 11) ? "danger" : "default"}
                placeholder={localize("companies-tax-number-placeholder") || "Vergi Numarası"}
                title={localize("companies-tax-number-input") || "Vergi Numarası *"}
                onChangeText={(text) => setTaxNumber(text)}
                spreadBehaviour="stretch"
                isRequired={true}
                value={taxNumber}
            />
            <View
                style={[
                    stylesheet.switchRow,
                    {
                        marginTop: spaces.spacingMd
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.switchTextContainer,
                        {
                            gap: spaces.spacingXs
                        }
                    ]}
                >
                    <Text
                        style={stylesheet.sectionTitle}
                        variant="titleMediumSize"
                    >
                        {localize("companies-is-exports-title") || "İhracat Yapıyor mu?"}
                    </Text>
                </View>
                <Switch
                    onPress={() => setIsExports(!isExports)}
                    customTheme={{
                        themeKey: "events_form" as never
                    }}
                    isActive={isExports}
                    type="neutral"
                />
            </View>
        </View>;
    };

    const renderOwnerSection = () => {
        return <View
            style={[
                stylesheet.sectionContainer,
                {
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.sectionHeader,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Text
                    style={stylesheet.sectionTitle}
                    variant="titleMediumSize"
                >
                    {localize("companies-owner-title") || "Firma Sahibi Bilgileri"}
                </Text>
            </View>
            <SelectBox
                onChange={(selected) => {
                    if (selected.length > 0 && selected[0]) {
                        const item = selected[0] as { __key?: string, value?: string };
                        setOwnerID(item.__key || item.value || "");
                    } else {
                        setOwnerID("");
                    }
                }}
                title={localize("companies-owner-select") || "Şirket Sahibi (Üye) *"}
                titleExtractor={(i: unknown) => (i as { title: string }).title}
                keyExtractor={(i: unknown) => (i as { value: string }).value}
                spreadBehaviour="stretch"
                ref={ownerSelectRef}
                isSearchable={true}
                data={usersData}
            />
            <SelectBox
                title={localize("companies-owner-position-select") || "Firma Sahibi Pozisyonu *"}
                onChange={(selected) => {
                    if (selected.length > 0 && selected[0]) {
                        const item = selected[0] as { __key?: string, value?: string };
                        setOwnerPositionID(item.__key || item.value || "");
                    } else {
                        setOwnerPositionID("");
                    }
                }}
                titleExtractor={(i: unknown) => (i as { title: string }).title}
                keyExtractor={(i: unknown) => (i as { value: string }).value}
                spreadBehaviour="stretch"
                ref={positionSelectRef}
                isRequired={!!ownerID}
                data={positionsData}
            />
        </View>;
    };

    const renderFooter = () => {
        return <View
            style={[
                stylesheet.footerActionContainer,
                {
                    paddingHorizontal: isMobile ? spaces.spacingMd : spaces.spacingXl,
                    flexDirection: isMobile ? "column-reverse" : "row",
                    backgroundColor: colors.project.brandSurface,
                    borderTopColor: colors.project.brandBorder,
                    paddingVertical: spaces.spacingLg,
                    borderTopWidth: borders.seperator,
                    gap: spaces.spacingMd
                }
            ]}
        >
            <Button
                onPress={() => navigation.navigate("CompaniesList")}
                spreadBehaviour={isMobile ? "stretch" : "free"}
                title={localize("cancel-button") || "İptal"}
                customBorderColor="secondaryButton"
                customColor="secondaryButton"
                variant="filled"
                size="small"
            />
            <Button
                icon={({
                    color,
                    size
                }) => <SaveIcon
                    color={colors.project.brandWhite || colors.content.icon[color]}
                    size={size}
                />}
                title={localize("companies-save-button") || "Kaydet"}
                spreadBehaviour={isMobile ? "stretch" : "free"}
                customTextColor={colors.project.brandWhite}
                isDisabled={!isFormValid}
                customBorderColor="gold"
                isLoading={isSubmitting}
                onPress={handleSubmit}
                customColor="gold"
                variant="filled"
                size="small"
            />
        </View>;
    };

    return <PageContainer
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.container,
                {
                    padding: isMobile ? spaces.spacingMd : spaces.spacingXl,
                    gap: isMobile ? spaces.spacingMd : spaces.spacingXl
                }
            ]}
        >
            {renderHeader()}
            <View
                style={[
                    stylesheet.formCard,
                    {
                        backgroundColor: colors.project.brandWhite,
                        borderColor: colors.project.brandBorder,
                        borderWidth: borders.seperator,
                        borderRadius: radiuses.lg
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.formBody,
                        {
                            padding: isMobile ? spaces.spacingMd : spaces.spacingXl,
                            gap: isMobile ? spaces.spacingMd : spaces.spacingXl
                        }
                    ]}
                >
                    {renderImagesSection()}
                    {renderBasicInfoSection()}
                    <Seperator
                        color="subtle"
                    />
                    {renderAddressTaxSection()}
                    <Seperator
                        color="subtle"
                    />
                    {renderOwnerSection()}
                </View>
                {renderFooter()}
            </View>
        </View>
        <Dialog
            content={localize("companies-create-confirm-title") || "Şirketi kaydetmek istediğinize emin misiniz?"}
            primaryButtonProps={{
                title: localize("companies-create-confirm-yes") || "Evet",
                onPress: handleConfirmSave
            }}
            secondaryButtonProps={{
                title: localize("companies-create-confirm-no") || "Hayır",
                onPress: () => setIsDialogVisible(false)
            }}
            isVisible={isDialogVisible}
            variant="yes-no"
        />
    </PageContainer>;
};

export default CompanyCreate;
